import {
    useState, useCallback, useMemo,
} from 'react'
import {
    useLocation,
} from 'react-router-dom'
import isEqual from 'lodash/isEqual'

import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useEnumValues from 'app/hooks/useEnumValues'
import useGetRegionalGroupById from 'app/Apps/AccountManagement/RegionalGroups/hooks/useGetRegionalGroupById'

import request from 'app/requests/Opportunity.requests'
import {
    OpportunityType,
} from '../opportunities.types'
import useAccountCollaborators from '../../hooks/useAccountCollaborators'

import getFields from './OpportunitiesForm.fields'
import useStyles from './OpportunitiesForm.style'

const defaultProps = {
    data: {},
    disabled: false,
    onSuccess: null,
    exists: false,
    customButtons: [],
}

export default function OpportunitiesForm({
    data,
    onSuccess,
    disabled,
    exists,
    customButtons,
}: Readonly<{
    data?: OpportunityType,
    onSuccess?: (opportunity?: OpportunityType) => void,
    disabled?: boolean,
    exists?: boolean,
    customButtons?: JSX.Element[],
}>) {
    const {
        state,
    } = useLocation()
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const [
        value,
        setValue,
    ] = useState<OpportunityType>(data as OpportunityType)

    const {
        data: regionalGroup,
    } = useGetRegionalGroupById(value?.regionalGroup?.id || state?.autofill?.regionalGroup?.id)

    const accountCollaborators = useAccountCollaborators(
        regionalGroup?.account?.id,
    )

    const onChange = useCallback((next) => {
        setValue((prev) => {
            let result = {
                ...prev,
                ...next,
            }

            if (!isEqual(next.regionalGroup?.id, prev?.regionalGroup?.id)) {
                result = {
                    ...result,
                    reprOriginAirport: undefined,
                    reprDestinationAirport: undefined,
                }
            }

            return result
        })
    }, [])

    const formValues = useMemo(() => {
        return {
            ...value,
            originRegion: regionalGroup?.originRegion,
            destinationRegion: regionalGroup?.destinationRegion,
            accountName: regionalGroup?.account?.companyName,
            temperatureRange: regionalGroup?.tempRange,
            account: regionalGroup?.account,
        }
    }, [
        regionalGroup,
        value,
    ])

    const showContainerInformation = disabled || !exists

    const fields = getFields({
        getAllowedValues,
        originRegion: formValues?.originRegion,
        destinationRegion: formValues?.destinationRegion,
        disableRegionalGroup: Boolean(state?.autofill?.regionalGroup?.id
            || value?.existNonCanceledLanes),
        disabled,
        showContainerInformation,
        accountId: regionalGroup?.account?.id ? [regionalGroup?.account?.id] : undefined,
        accountCollaborators,
    })
    const fieldsWithClasses = useFieldsWithClassName(fields, classes)

    return (
        <FormWithControls
            onSuccess={onSuccess}
            value={formValues}
            setExternalValue={onChange}
            name={request.domainName}
            fields={fieldsWithClasses}
            disabled={disabled}
            customButtons={customButtons}
            // TODO-DK: move this logic out of Form
            requestParams={exists ? request.UpdateOpportunity({
                opportunityId: data.id,
            }) : request.CreateOpportunity()}
            classNames={{
                gridWrapper: showContainerInformation
                    ? classes.gridWrapperWithContainerInformation
                    : classes.gridWrapper,
            }}
            exists={exists}
        />
    )
}

OpportunitiesForm.defaultProps = defaultProps
